import customerRideApi from 'api/customerRideApi';
import ToastHelper from 'general/helpers/ToastHelper';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { formatRideDateTime, formatRideMoney, newIdempotencyKey, rideErrorMessage } from '../rideUtils';
import RideAppIcon from './RideAppIcon';
import RideMapPickerSheet from './RideMapPickerSheet';
import RidePlaceSearchSheet from './RidePlaceSearchSheet';
import RidePortal from './RidePortal';

const RideBookingModal = ({ trip, searchPayload, maxSeats, onClose, onBooked }) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('BIDV');
  const [promoCode, setPromoCode] = useState('');
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [selectedSeats, setSelectedSeats] = useState(searchPayload.seats || 1);
  const [pickup, setPickup] = useState(searchPayload.pickup);
  const [dropoff, setDropoff] = useState(searchPayload.dropoff);
  const [editingTarget, setEditingTarget] = useState(null);
  const [mapTarget, setMapTarget] = useState(null);
  const [createdBooking, setCreatedBooking] = useState(null);
  const quoteRequestRef = useRef(0);
  const promoRequestRef = useRef(0);
  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);
  const seatLimit = Math.max(1, Number(maxSeats || trip.seatsAvailable || selectedSeats));

  const fetchQuote = async () => {
    const requestId = ++quoteRequestRef.current;
    const promoRequestId = ++promoRequestRef.current;
    setLoading(true);
    setError('');
    setPromo(null);
    try {
      const response = await customerRideApi.createQuote(trip.tripId, {
        pickup,
        dropoff,
        seats: selectedSeats,
        isCharter: searchPayload.isCharter,
        ...(searchPayload.isCharter ? { charterVehicleCapacity: searchPayload.charterVehicleCapacity } : {}),
      });
      if (requestId !== quoteRequestRef.current) return;
      setQuote(response.quote);
      setNow(Date.now());
      if (promoCode.trim()) {
        try {
          const promoRes = await customerRideApi.checkPromoCode({
            code: promoCode.trim(),
            totalFareBeforeDiscount: response.quote.totalFare,
            seats: selectedSeats,
            isCharter: searchPayload.isCharter,
            pickup: response.quote.pickup,
            dropoff: response.quote.dropoff,
          });
          if (requestId === quoteRequestRef.current && promoRequestId === promoRequestRef.current) {
            setPromo(promoRes);
          }
        } catch {
          if (promoRequestId === promoRequestRef.current) setPromo(null);
        }
      }
    } catch (requestError) {
      if (requestId === quoteRequestRef.current) {
        setQuote(null);
        setError(rideErrorMessage(requestError));
      }
    } finally {
      if (requestId === quoteRequestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuote();
  }, [trip.tripId, selectedSeats, pickup, dropoff]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const secondsLeft = quote ? Math.max(0, Math.floor((new Date(quote.expiresAt).getTime() - now) / 1000)) : 0;
  // `discountAmount` is already the authoritative trip-level discount from
  // the backend for FIXED, PERCENT and TARGET_FARE promos.
  const effectiveDiscount = promo && quote
    ? Math.max(0, Math.min(Number(quote.totalFare || 0), Number(promo.discountAmount || 0)))
    : 0;
  const payable = Math.max(0, Number(quote?.totalFare || 0) - effectiveDiscount);

  const refreshBidvBooking = useCallback(async () => {
    if (!createdBooking?.bookingId || createdBooking.paymentStatus !== 'PENDING') return;
    try {
      const response = await customerRideApi.getBooking(createdBooking.bookingId);
      const updated = response?.booking;
      if (!updated) return;
      setCreatedBooking(updated);
      if (updated.paymentStatus === 'PAID') {
        ToastHelper.showSuccess('BIDV đã xác nhận thanh toán. Yêu cầu đã được gửi tới tài xế.');
        onBooked(updated);
      }
    } catch (requestError) {
      setError(rideErrorMessage(requestError, 'Chưa kiểm tra được trạng thái thanh toán.'));
    }
  }, [createdBooking, onBooked]);

  useEffect(() => {
    if (!createdBooking?.bookingId || createdBooking.paymentStatus !== 'PENDING') return undefined;
    const timer = window.setInterval(refreshBidvBooking, 5000);
    return () => window.clearInterval(timer);
  }, [createdBooking?.bookingId, createdBooking?.paymentStatus, refreshBidvBooking]);

  const checkPromo = async () => {
    if (!quote || loading || !promoCode.trim()) return;
    const requestId = ++promoRequestRef.current;
    setPromoLoading(true);
    setError('');
    try {
      const response = await customerRideApi.checkPromoCode({
        code: promoCode.trim(),
        totalFareBeforeDiscount: quote.totalFare,
        seats: selectedSeats,
        isCharter: searchPayload.isCharter,
        pickup: quote.pickup,
        dropoff: quote.dropoff,
      });
      if (requestId === promoRequestRef.current) setPromo(response);
    } catch (requestError) {
      if (requestId === promoRequestRef.current) {
        setPromo(null);
        setError(rideErrorMessage(requestError));
      }
    } finally {
      if (requestId === promoRequestRef.current) setPromoLoading(false);
    }
  };

  const submit = async () => {
    if (!quote || loading || secondsLeft <= 0 || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await customerRideApi.createBooking({
        quoteId: quote.quoteId,
        paymentMethod,
        ...(promoCode.trim() ? { promoCode: promoCode.trim() } : {}),
      }, idempotencyKey);
      if (response.booking?.paymentMethod === 'BIDV' && response.booking?.paymentStatus === 'PENDING') {
        setCreatedBooking(response.booking);
        return;
      }
      ToastHelper.showSuccess('Đã gửi yêu cầu đặt chỗ. Vui lòng chờ tài xế xác nhận.');
      onBooked(response.booking);
    } catch (requestError) {
      setError(rideErrorMessage(requestError));
    } finally {
      setSubmitting(false);
    }
  };

  if (createdBooking?.paymentMethod === 'BIDV' && createdBooking.paymentStatus === 'PENDING') {
    const bidvPayment = createdBooking.bidvPayment || {};
    const paymentAmount = Math.max(
      0,
      Number(createdBooking.totalFare || payable || 0) - Number(createdBooking.promoDiscountAmount || 0),
    );
    return (
      <RidePortal>
        <div className="customer-ride-modal-backdrop" role="presentation">
          <section className="customer-ride-modal ride-bidv-payment-modal" role="dialog" aria-modal="true" aria-label="Thanh toán chuyển khoản BIDV">
            <button className="customer-ride-modal-close" type="button" aria-label="Đóng" onClick={onClose}><RideAppIcon name="close" /></button>
            <div className="customer-ride-modal-heading"><span className="customer-ride-modal-icon"><RideAppIcon name="bidv" /></span><div><h2>Chuyển khoản BIDV</h2><p>Quét mã QR trong ứng dụng ngân hàng để thanh toán đặt chỗ.</p></div></div>
            <div className="customer-ride-quote-route"><div><RideAppIcon name="pickup" /><span>{createdBooking.pickup?.address || pickup?.address}</span></div><div><RideAppIcon name="destination" /><span>{createdBooking.dropoff?.address || dropoff?.address}</span></div></div>
            <section className="ride-bidv-payment-card">
              <div className="ride-bidv-amount"><span>Tổng thanh toán</span><strong>{formatRideMoney(paymentAmount, createdBooking.currency || 'VND')}</strong></div>
              {bidvPayment.qrCode ? <QRCode value={String(bidvPayment.qrCode)} size={190} /> : <div className="ride-bidv-qr-placeholder"><RideAppIcon name="warning" /> Chưa tạo được mã QR BIDV</div>}
              <div className="ride-bidv-bank-info">
                <strong>Ngân hàng BIDV</strong>
                {bidvPayment.accountNumber && <p><span>Số tài khoản</span><b>{bidvPayment.accountNumber}</b></p>}
                {bidvPayment.accountName && <p><span>Chủ tài khoản</span><b>{bidvPayment.accountName}</b></p>}
                <p><span>Mã đặt chỗ</span><b>{createdBooking.bookingId}</b></p>
              </div>
            </section>
            {error && <div className="customer-ride-alert danger">{error}</div>}
            <p className="ride-bidv-payment-hint"><RideAppIcon name="time" className="ride-icon-spin" /> Đang chờ BIDV xác nhận. Tài xế chỉ nhận được yêu cầu sau khi thanh toán thành công.</p>
            <button type="button" className="customer-ride-secondary full" onClick={refreshBidvBooking}>Tôi đã chuyển khoản · Kiểm tra lại</button>
            <button type="button" className="customer-ride-primary full" onClick={onClose}>Để thanh toán sau</button>
          </section>
        </div>
      </RidePortal>
    );
  }

  if (createdBooking?.paymentMethod === 'BIDV'
    && ['FAILED', 'REFUNDED'].includes(createdBooking.paymentStatus)) {
    return (
      <RidePortal>
        <div className="customer-ride-modal-backdrop" role="presentation">
          <section className="customer-ride-modal ride-scheduled-modal--success" role="dialog" aria-modal="true" aria-label="Thanh toán BIDV đã hết hạn">
            <div className="ride-scheduled-success-icon"><RideAppIcon name="warning" /></div>
            <h2>Thanh toán đã hết hạn</h2>
            <p>Ghế đã được nhả và yêu cầu chưa gửi tới tài xế. Vui lòng chọn lại chuyến để tạo mã QR mới.</p>
            <button type="button" className="customer-ride-primary full" onClick={onClose}>Đóng và chọn lại chuyến</button>
          </section>
        </div>
      </RidePortal>
    );
  }

  return (
    <RidePortal>
      <div className="customer-ride-modal-backdrop" role="presentation" onMouseDown={onClose}>
        <div className="customer-ride-modal customer-ride-booking-modal" role="dialog" aria-modal="true" aria-label="Yêu cầu đặt chỗ" onMouseDown={(event) => event.stopPropagation()}>
          <button className="customer-ride-modal-close" type="button" aria-label="Đóng" onClick={onClose}><RideAppIcon name="close" /></button>
          <div className="customer-ride-modal-heading"><span className="customer-ride-modal-icon"><RideAppIcon name="car" /></span><div><h2>Yêu cầu đặt chỗ</h2><p>{formatRideDateTime(trip.departureAt)}</p></div></div>
          {trip.driver?.fullname && <div className="customer-ride-booking-context"><strong>Tài xế: {trip.driver.fullname}</strong>{trip.vehicle && <span>{[trip.vehicle.brand, trip.vehicle.model, trip.vehicle.plate].filter(Boolean).join(' · ')}</span>}</div>}
          {trip.origin?.address && trip.destination?.address && <div className="customer-ride-quote-route"><div><RideAppIcon name="pickup" /><span>Điểm xuất phát: {trip.origin.address}</span></div><div><RideAppIcon name="destination" /><span>Điểm kết thúc: {trip.destination.address}</span></div></div>}
          {loading && !quote ? <div className="customer-ride-loading"><RideAppIcon name="time" className="ride-icon-spin" /> Đang lấy báo giá mới nhất...</div> : error && !quote ? <div className="customer-ride-alert danger">{error}<div style={{ marginTop: 8 }}><button type="button" className="customer-ride-secondary" onClick={fetchQuote}><RideAppIcon name="sync" /> Thử lại</button></div></div> : quote && (
            <>
              <div className="customer-ride-booking-context"><strong>Điểm đón / trả của bạn</strong><span>Tài xế sẽ xác nhận sau khi bạn gửi yêu cầu.</span></div>
              <div className="customer-ride-quote-route customer-ride-editable-route"><button type="button" onClick={() => setEditingTarget('PICKUP')}><RideAppIcon name="pickup" /><span>{quote.pickup?.address}</span><small>Sửa điểm đón</small></button><button type="button" onClick={() => setEditingTarget('DROPOFF')}><RideAppIcon name="destination" /><span>{quote.dropoff?.address}</span><small>Sửa điểm trả</small></button></div>
              {seatLimit > 1 && !searchPayload.isCharter && <div className="customer-ride-seat-picker"><span>Số ghế đặt</span><div><button type="button" aria-label="Giảm số ghế" disabled={selectedSeats <= 1} onClick={() => { promoRequestRef.current += 1; setPromo(null); setSelectedSeats((value) => value - 1); }}>−</button><strong>{selectedSeats}</strong><button type="button" aria-label="Tăng số ghế" disabled={selectedSeats >= seatLimit} onClick={() => { promoRequestRef.current += 1; setPromo(null); setSelectedSeats((value) => value + 1); }}>+</button></div><small>Còn tối đa {seatLimit} ghế</small></div>}
              <div className="customer-ride-quote-summary"><span><small>Số ghế</small><b>{quote.seats} ghế</b></span><span><small>Giá mỗi ghế</small><b>{formatRideMoney(quote.farePerSeat, quote.currency)}</b></span></div>
              {loading && <p className="customer-ride-quote-refreshing"><RideAppIcon name="time" className="ride-icon-spin" /> Đang cập nhật báo giá...</p>}
              {secondsLeft <= 0 && (
                <div className="customer-ride-alert danger" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span>Báo giá chuyến xe đã hết hạn. Vui lòng cập nhật báo giá mới.</span>
                  <button type="button" className="customer-ride-secondary" style={{ padding: '4px 10px', fontSize: 13, whiteSpace: 'nowrap' }} onClick={fetchQuote}>
                    <RideAppIcon name="sync" /> Cập nhật
                  </button>
                </div>
              )}
              <div className="customer-ride-payment">
                <h3>Phương thức thanh toán</h3>
                <label className={paymentMethod === 'BIDV' ? 'selected' : ''}>
                  <input type="radio" name="ride-payment" value="BIDV" aria-label="Chuyển khoản BIDV" disabled={submitting} checked={paymentMethod === 'BIDV'} onChange={() => setPaymentMethod('BIDV')} />
                  <RideAppIcon name="bidv" className="ride-payment-icon" />
                  <span><b>Chuyển khoản BIDV</b><small>Quét QR, chỉ gửi tài xế sau khi BIDV xác nhận</small></span>
                </label>
                <label className={paymentMethod === 'CASH' ? 'selected' : ''}>
                  <input type="radio" name="ride-payment" value="CASH" aria-label="Tiền mặt" disabled={submitting} checked={paymentMethod === 'CASH'} onChange={() => setPaymentMethod('CASH')} />
                  <RideAppIcon name="cash" />
                  <span><b>Tiền mặt</b><small>Thanh toán trực tiếp cho tài xế</small></span>
                </label>
                <label className={paymentMethod === 'WALLET' ? 'selected' : ''}>
                  <input type="radio" name="ride-payment" value="WALLET" aria-label="Ví Xe ghép" disabled={submitting} checked={paymentMethod === 'WALLET'} onChange={() => setPaymentMethod('WALLET')} />
                  <RideAppIcon name="rideWallet" />
                  <span><b>Ví Xe ghép</b><small>Số dư được giữ đến khi tài xế xác nhận</small></span>
                </label>
              </div>
              <div className="customer-ride-promo"><input value={promoCode} disabled={submitting} onChange={(event) => { promoRequestRef.current += 1; setPromoLoading(false); setPromoCode(event.target.value.toUpperCase()); setPromo(null); }} placeholder="Nhập mã ưu đãi" /><button type="button" disabled={loading || submitting || promoLoading || !promoCode.trim()} onClick={checkPromo}>{promoLoading ? 'Đang kiểm tra' : 'Áp dụng'}</button></div>
              {promo && <div className="customer-ride-alert success">Đã giảm {formatRideMoney(effectiveDiscount, quote.currency)}. Mã sẽ được xác thực lại khi đặt chỗ.</div>}
              {error && <div className="customer-ride-alert danger">{error}</div>}
              <div className="customer-ride-total"><span>Tổng thanh toán</span><strong>{formatRideMoney(payable, quote.currency)}</strong></div>
              {secondsLeft <= 0 ? (
                <button className="customer-ride-secondary full" type="button" onClick={fetchQuote}>
                  <RideAppIcon name="sync" /> Cập nhật báo giá mới
                </button>
              ) : (
                <button className="customer-ride-primary full" type="button" disabled={loading || submitting} onClick={submit}>
                  {submitting ? <><RideAppIcon name="time" className="ride-icon-spin" /> Đang gửi...</> : 'Gửi yêu cầu đặt chỗ'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {editingTarget && <RidePlaceSearchSheet
        target={editingTarget}
        currentLocation={pickup}
        value={editingTarget === 'PICKUP' ? pickup : dropoff}
        onClose={() => setEditingTarget(null)}
        onOpenMap={(target) => { setEditingTarget(null); setMapTarget(target); }}
        onSelect={(point) => {
          promoRequestRef.current += 1;
          setPromo(null);
          if (editingTarget === 'PICKUP') setPickup(point);
          else setDropoff(point);
          setEditingTarget(null);
        }}
      />}
      {mapTarget && <RideMapPickerSheet
        target={mapTarget}
        initialPoint={mapTarget === 'PICKUP' ? pickup : dropoff}
        onClose={() => setMapTarget(null)}
        onConfirm={(point) => {
          promoRequestRef.current += 1;
          setPromo(null);
          if (mapTarget === 'PICKUP') setPickup(point);
          else setDropoff(point);
          setMapTarget(null);
        }}
      />}
    </RidePortal>
  );
};

export default RideBookingModal;
